import React from "react";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import type { ActiveDialog } from "src/interfaces/adminHtmlState.interface";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { closeGlobalTour, requestGlobalTourStep } from "src/store/tour/actions";

interface TourStepProps {
  title: string;
  text: string;
  current: number;
  total: number;
  isFirst: boolean;
  isLast: boolean;
  stepIndex: number;
}

function TourStep({
  title,
  text,
  current,
  total,
  isFirst,
  isLast,
  stepIndex,
}: TourStepProps) {
  const dispatch = useDispatch();
  const history = useHistory();
  return (
    <>
      <DialogTitle>
        {title} - {current}/{total}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{text}</DialogContentText>
      </DialogContent>
      <DialogActions>
        {!isFirst && (
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              dispatch(requestGlobalTourStep(stepIndex - 1, history))
            }
          >
            Back
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={
            isLast
              ? () => dispatch(closeGlobalTour())
              : () => dispatch(requestGlobalTourStep(stepIndex + 1, history))
          }
        >
          {isLast ? "Done" : "Next"}
        </Button>
      </DialogActions>
    </>
  );
}

interface StepInput {
  text: string;
  selector?: string;
  highlightedSelectors?: string[];
  navigate?: string;
  dialog?: ActiveDialog;
}

export interface TourStepData {
  selector?: string;
  highlightedSelectors?: string[];
  navigate?: string;
  dialog?: ActiveDialog;
  content: () => React.ReactElement;
}

function createSteps(title: string, ...steps: StepInput[]): TourStepData[] {
  return steps.map((s, index) => ({
    selector: s.selector,
    highlightedSelectors: s.highlightedSelectors,
    navigate: s.navigate,
    dialog: s.dialog,
    content: () => {
      const current = index + 1;
      return (
        <TourStep
          title={title}
          text={s.text}
          current={current}
          total={steps.length}
          isFirst={current === 1}
          isLast={current === steps.length}
          stepIndex={index}
        />
      );
    },
  }));
}

export const TOUR_STEPS = {
  "connect-bank": createSteps(
    "Connect Bank",
    {
      text: 'To connect new banks/credit cards, click the "Integrations" tab.',
      selector: "#integrations",
    },
    {
      text: "Click here to connect your bank/credit card.",
      selector: "#connect-bank-account-button",
      navigate: "/integrations",
    }
  ),
  viewing_data: createSteps(
    "Viewing Data",
    {
      text: "Click here to view your transaction details.",
      selector: "#transaction-details",
    },
    {
      text: "Use these options to specify the data you want to see.",
      selector: "#transaction-details-page-buttons",
      navigate: "/transaction-details",
    },
    {
      text: 'You can use "Views" to look at different groupings of transactions.',
      selector: "#transaction-details-sales-view-select",
      navigate: "/transaction-details/sales",
    },
    {
      text: "Quickly view transactions from different time periods using these buttons.",
      selector: "#transaction-details-time-select",
    },
    {
      text: "Apply custom filtering to select specific transactions.",
      selector: '[aria-label="Show filters"]',
    }
  ),
  analytics: createSteps(
    "Analytics",
    {
      text: "Click here to view your dashboards.",
      selector: "#management",
    },
    {
      text: "Use this dashboard for quick insight into your entire company. Remember -- Profit is what we're trying to maximize!",
      selector: "#dashboards-overview-cards",
      navigate: "/management",
    },
    {
      text: 'To dig deeper, select the "Sales Analytics" view.',
      selector: "#dashboard-view-select",
    },
    {
      text: "Study different time periods using the quick filters.",
      selector: "#transaction-details-time-select",
    },
    {
      text: "Dig deeper with advanced filtering to gain more understanding about your sales.",
      selector: "#advanced-filtering-card",
    }
  ),
  chart_of_accounts: createSteps(
    "Chart of Accounts",
    {
      text: "Click here to open the Add Transaction Form.",
      selector: "#add_expense",
    },
    {
      text: "Click here to view accounts and create new ones.",
      selector: "#general-ledger-row",
      dialog: "add_expense",
    },
    {
      text: 'Select "Add New Account" to create general ledger accounts.',
      selector: "#add_new_account",
      dialog: "add_expense",
    }
  ),
  reconciliation: createSteps(
    "Reconciliation",
    {
      text: "Click here to reconcile sale platforms, bank accounts, and credit cards.",
      selector: "#reconciliation",
    },
    {
      text: 'You can reconcile any sale platform, bank account, or credit card by clicking the "Start Reconciling" button. Check out our help center for more info on this process!',
      selector: ".reconciliation-container",
      navigate: "/reconciliation",
    }
  ),
  reports: createSteps(
    "Reports",
    {
      text: "Access reports for your business with the Reseller Reports tab.",
      selector: "#reseller_reports",
    },
    {
      text: "Select your report type here.",
      selector: "#report-type-dropdown",
      navigate: "/reports",
    },
    {
      text: "Set the time period for the report here.",
      selector: "#report-time-selection",
    },
    {
      text: "Click here to run your report.",
      selector: "#run-report-button",
    }
  ),
  Amazon: createSteps(
    "Connect Amazon",
    {
      text: 'To connect to your Amazon account, click the "Integrations" tab.',
      selector: "#integrations",
    },
    {
      text: "Click here to add the account.",
      selector: "#add-integration-Amazon",
      navigate: "/integrations",
    }
  ),
  Ebay: createSteps(
    "Connect eBay",
    {
      text: 'To connect to your eBay account, click the "Integrations" tab.',
      selector: "#integrations",
    },
    {
      text: "Click here to add the account.",
      selector: "#add-integration-eBay",
      navigate: "/integrations",
    }
  ),
  Poshmark: createSteps(
    "Connect Poshmark",
    {
      text: 'To connect to your Poshmark account, click the "Integrations" tab.',
      selector: "#integrations",
    },
    {
      text: "Click here to add the account.",
      selector: "#add-integration-Poshmark",
      navigate: "/integrations",
    }
  ),
  Mercari: createSteps(
    "Connect Mercari",
    {
      text: 'To connect to your Mercari account, click the "Integrations" tab.',
      selector: "#integrations",
    },
    {
      text: "Click here to add the account.",
      selector: "#add-integration-Mercari",
      navigate: "/integrations",
    }
  ),
  Etsy: createSteps(
    "Connect Etsy",
    {
      text: 'To connect to your Etsy account, click the "Integrations" tab.',
      selector: "#integrations",
    },
    {
      text: "Click here to add the account.",
      selector: "#add-integration-Etsy",
      navigate: "/integrations",
    }
  ),
  Whatnot: createSteps(
    "Connect Whatnot",
    {
      text: 'To connect to your Whatnot account, click the "Integrations" tab.',
      selector: "#integrations",
    },
    {
      text: "Click here to add the account.",
      selector: "#add-integration-Whatnot",
      navigate: "/integrations",
    }
  ),
  "connect-platforms": createSteps(
    "Connect Platforms",
    {
      text: 'Click the "Integrations" tab to manage your platform connections.',
      selector: "#integrations",
    },
    {
      text: "From here you can add integrations and choose whether to import your past data.",
      selector: "#platform-integrations-table",
      navigate: "/integrations",
    }
  ),
  "manual-upload": createSteps(
    "Upload Your Data",
    {
      text: "Use these buttons to manually add inventory, report sales, and record transactions.",
      selector: "#report_sale",
      highlightedSelectors: ["#report_sale", "#add_inventory", "#add_expense"],
    },
    {
      text: 'For each button, you can use the Bulk Upload to upload a spreadsheet with data. If you prefer, you can also enter data manually by filling out the form and clicking "Add".',
      selector: "#transaction-edit-dialog-bulk-upload-button",
      dialog: "add_expense",
    }
  ),
};

export type TourStepKey = keyof typeof TOUR_STEPS;
